import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import {
  CompanyChangeOfficerAppointComponent,
  getPositionTypesOptions,
} from '../../asic-forms/company/company-change-officer-appoint/company-change-officer-appoint.component';
import { enumToSelectOptions } from '../../../shared/utils/enums-to-list-formatter';
import { Company } from '@models/companies/company';
import { Document } from '@models/documents/document';
import { EntityChangeData } from '@models/documents/entity-change-data';
import { CompanyChangeData } from '@models/ui-models/company-change-data';
import { CompanyChangeOfficerType } from '@models/relationships/company-change-officer-type';
import { RelationshipType, RelationshipTypeLabels } from '@models/relationships/relationship-type.enum';
import { OfficerRelationshipDetails, Relationship } from '@models/relationships/relationship';
import { SelectOption } from '@models/ui-models/select-options';
import { ChangeDictionaryHelper } from '@models/ui-models/change-dictionary-helper.model';
import { CountriesService } from "../../../shared/services/countries.service";

export class CompanyChangeOfficer extends EntityChangeData {
  static override readonly $type = 'CompanyChangeOfficer';
  offices: Relationship[];
  actionType: CompanyChangeOfficerType;
  cessationReason: ReasonOfCessation | null;
  noticeOfResignation: string | null;

  constructor(data: Partial<CompanyChangeOfficer> = {}) {
    super(data);

    this.offices = data.offices?.filter(Boolean) ?? [];
    this.actionType = data.actionType ?? CompanyChangeOfficerType.Appointment;
    this.cessationReason = data.cessationReason ?? null;
    this.noticeOfResignation = data.noticeOfResignation ?? null;
  }

  override toDictionary(): { key: string; value: string; }[] {
    const dict = new ChangeDictionaryHelper();
    const officeholder = this.offices[0] ?? new Relationship();
    const officeholderName = officeholder?.individualDataOverride?.fullName.trim() ?? '';
    const positions = this.offices
      .map((relationship) => RelationshipTypeLabels[relationship.type])
      .join(', ');
    const officerWithRole = (role: RelationshipType) => this.offices.find(relationship => relationship.type === role);
    if (this.actionType === CompanyChangeOfficerType.Removal) {
      // B1 - Resignation
      // Officer Name
      // Position
      // Date of Cessation
      // Reason of Cessation
      // Notice of Resignation (If reason of cessation is Resignation)

      dict
        .add('Name of Officeholder', officeholder.individualDataOverride?.fullName.trim() || '')
        .add('Position', positions)
        .addDate('Date of Cessation', this.changeDate)
        .add('Reason of Cessation', ReasonOfCessation[this.cessationReason as ReasonOfCessation] ?? '')
        .addIfValueIsNotEmpty('Notice of Resignation', this.noticeOfResignation?.toString()?.trim() ?? '');
    } else if (this.actionType === CompanyChangeOfficerType.Appointment) {
      // B2 - Appointment
      // Date of Appointment
      // Name of Officeholder
      // Former Name
      // Address
      // Date of Birth
      // Place of Birth(Country/State)
      // PLace of Birth (Suburb/City)
      // Position
      // Director ID
      // The appointed "Alternate Director" is alternate for
      // Expiry Date
      // Terms of Appointment

      const alternativeDirectorOfficer = officerWithRole(RelationshipType.AlternativeDirector);
      const directorOfficer = officerWithRole(RelationshipType.Director);
      const birthCountryName = officeholder?.individualDataOverride?.birthCountry ?? '';
      const birthCountry = (CountriesService as { getAustralianCodeByStateName: (countryName: string) => string })
        .getAustralianCodeByStateName(birthCountryName) || birthCountryName;

      dict
        .addDate('Date of Appointment', this.changeDate)
        .addEmptyLine()
        .add('Name of Officeholder', officeholderName)
        .add('Former Name', officeholder?.individualDataOverride?.formerName?.fullName ?? '')
        .add('Address', officeholder?.individualDataOverride?.address?.normalizedFullAddress ?? '')
        .addDate('Date of Birth', officeholder?.individualDataOverride?.dob)
        .addCountry('Place of Birth (Country / State)', birthCountry)
        .add('Place of Birth (Suburb / City)', officeholder?.individualDataOverride?.birthCity ?? '')
        .add('Position', positions);

      if (directorOfficer || alternativeDirectorOfficer) {
        dict.addDin('Director ID', (directorOfficer || alternativeDirectorOfficer)?.individualDataOverride?.din?.trim());
      }

      if (alternativeDirectorOfficer) {
        dict.add('The appointed "Alternate director" is alternate for', (alternativeDirectorOfficer?.details as OfficerRelationshipDetails)?.alternativeDirectorFor || '');

        if ((officeholder.details as OfficerRelationshipDetails)?.expiryDate) {
          dict.addDate('Expiry Date', (officeholder.details as OfficerRelationshipDetails)?.expiryDate);
        }

        dict.add('Terms of Appointment', (alternativeDirectorOfficer?.details as OfficerRelationshipDetails)?.termsOfAppointment || '');
      }
    }

    return dict.dictionary;
  }

  override fillModal(modalRef: NgbModalRef, document: Document, actualCompany: Company): NgbModalRef {
    modalRef = this.fillModalWithCompany(modalRef, actualCompany, true);
    const instance = (modalRef.componentInstance as CompanyChangeOfficerAppointComponent);
    instance.companyChangeData.documentId = document.documentId;

    this.offices = this.offices
      .map(officer => {
        officer.key = officer.individualId ?? instance.officersSelectOptions
          .find(option => option.label === officer?.individualDataOverride?.fullName)?.value as string ?? '';
        return officer;
      })
      .filter((officer) => getPositionTypesOptions().find((allowedType) => allowedType.value == officer.type));

    return modalRef;
  }

  fillModalWithCompany(modalRef: NgbModalRef, company: Company, isEdit = false): NgbModalRef {
    const instance = (modalRef.componentInstance as CompanyChangeOfficerAppointComponent);

    instance.isEdit = isEdit;
    instance.officeholders = company.officers;
    instance.formModel = this;
    instance.companyChangeData = new CompanyChangeData(company);

    const directorList = company.officers?.filter(officer => officer.type === RelationshipType.Director && !officer.end);
    instance.moreThenOneDirectorExist = directorList?.length > 1;

    // TODO: remove after individualId works fine
    const officerIndividualsIdDictionary: Record<string, string> = {};

    instance.officersSelectOptions = structuredClone(company.officers.filter(o => !o.end))
      .reduce((officersSelectOptions: SelectOption[], officer) => {
          // TODO: remove after individualId works fine
          if (!officerIndividualsIdDictionary[officer.individualDataOverride!.fullName]) {
            officerIndividualsIdDictionary[officer.individualDataOverride!.fullName] = Math.random().toString(36).substring(7);
          }

          // TODO: remove after individualId works fine
          officer.key = officer.individualId ?? officerIndividualsIdDictionary[officer.individualDataOverride!.fullName];

          const officerOption: SelectOption = {
            label: officer.individualDataOverride!.fullName,
            value: officer.key,
          };

          if (!instance.officers[officer.key]) {
            instance.officers[officer.key] = [];

            officersSelectOptions?.push(officerOption);
          }

          if (officer.type === RelationshipType.Director) {
            instance.allDirectorsOptions?.push(officerOption);
          }

          instance.officers[officer.key]?.push(officer);

          return officersSelectOptions;
        },
        []
      )
      .sort((a, b) => a.label?.toLowerCase().localeCompare(b.label?.toLowerCase()));

    instance.officersSelectOptions = instance.officersSelectOptions.map((officerOption) => {
      const officeholderTypes = (instance.officers[officerOption.value as string] as Relationship[]).map((officer) => officer.type);
      const officeholderTypesWithLabels = officeholderTypes.map((type) => RelationshipTypeLabels[type]);
      const label = `${ officerOption.label } (${ officeholderTypesWithLabels.join(', ') })`;

      return {
        label,
        value: officerOption.value,
        disabled: officerOption.disabled
      } as SelectOption;
    });

    return modalRef;
  }

  override buildPreviewType(): string {
    return this.changeType
      .replace(this.actionType == CompanyChangeOfficerType.Appointment ? '1' : '2', '')
      .replace(':b', 'B');
  }

  override prepareToRequest(): CompanyChangeOfficer {
    return {
      ...this,
      ...super.prepareToRequest(),
      offices: this.offices.map((officer) => Relationship.prepareToRequest(officer))
    };
  }
}

export enum ReasonOfCessation {
  Resignation = 0,
  Removal = 1,
  Death = 2
}

export const reasonOfCessationOptions: SelectOption[] = enumToSelectOptions(ReasonOfCessation);
